import CheckIcon from '@mui/icons-material/Check';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import { FormControlLabel, ListItemButton, ListItemIcon, ListItemText, Stack, Switch, Typography } from '@mui/material';
import { AbsDropDown } from 'customer_portal-ui-shared';
import { useEffect, useState } from 'react';
import { AbsColor } from '../../constants';
import { ICalendarRiskGroup } from '../../contracts/calendar-risk-group.contract';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Loading } from '../shared';
import CalendarRiskListItem from './calendar-risk-list-item.component';
import { ShowGuidelinesActions } from '../../features/showGuidelines.feature';

const CriteriaFilterStyle = {
    IconStyle: {
        color: AbsColor.White1
    },
    MenuStyle: {
        backgroundColor: AbsColor.Blue1,
        color: AbsColor.White1,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        width: '100%'
    }
};

const CalendarSurveyRiskList = () => {
    const dispatch = useAppDispatch();

    const selectedVessels = useAppSelector((state) => state.Calendar.Filters.Vessels);
    const isLoading = useAppSelector((state) => state.Calendar.Risk.IsLoading);
    const showGuidelines = useAppSelector((state) => state.ShowGuidelines.showGuideLines);
    const surveyFilter = useAppSelector((state) => state.Calendar.Filters.Surveys);
    const selectedVessel = useAppSelector((state) => state.Calendar.Filters.Vessels);

    const [regulations, setRegulations] = useState<ICalendarRiskGroup[]>([]);
    const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);
    const [years, setYears] = useState<string[] | undefined>(undefined);

    useEffect(() => {
        if (!surveyFilter || surveyFilter.length <= 0) {
            setRegulations([]);
            return;
        }

        const tempYears = Array.from(new Set(surveyFilter.map((s) => s.SurveyDueDate.split('-')[0])));
        setYears(tempYears);

        if (!selectedYear) setSelectedYear(tempYears[0]);

        if (selectedVessel && selectedVessel.length > 0 && selectedYear !== tempYears[tempYears.length - 1]) setSelectedYear(tempYears[tempYears.length - 1]);

        const tempRegulations: ICalendarRiskGroup[] = tempYears.map((year) => {
            const regulations = showGuidelines
                ? surveyFilter.filter((f) => f.SurveyDueDate.split('-')[0] === year).flatMap((f) => f.Regulations)
                : surveyFilter
                      .filter((f) => f.SurveyDueDate.split('-')[0] === year)
                      .flatMap((f) => f.Regulations)
                      .filter((r) => !r.RegulationName.includes('Guideline'));
            return {
                Year: year.toString(),
                HighRisks: regulations.map((regulation) => ({
                    Regulation: regulation,
                    Vessels: selectedVessel ?? []
                })),
                MediumRisks: [],
                LowRisks: []
            };
        });

        setRegulations(tempRegulations);
    }, [surveyFilter, showGuidelines, selectedYear]);

    const getHeaderTitle = (): string => {
        if (selectedVessels && selectedVessels.length > 0) {
            return `${selectedVessels.length} Vessel${selectedVessels.length > 1 ? 's' : ''} Selected`;
        }

        return `All Vessels Selected`;
    };

    return (
        <Stack direction={'column'} style={{ width: '100%' }} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1} style={{ width: '100%', height: '50px' }}>
                <Stack direction={'row'}>
                    <DirectionsBoatFilledIcon style={{ color: AbsColor.Blue1 }} />
                    <div>{getHeaderTitle()}</div>
                </Stack>
                <Stack direction={'row'} alignItems={'center'} gap={5}>
                    {years && (
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            <Typography>Year:</Typography>
                            <AbsDropDown
                                selected={selectedYear}
                                handleChange={(e: { label: string; value: string }) => setSelectedYear(e.value)}
                                options={years.map((m) => {
                                    return {
                                        label: m,
                                        value: m
                                    };
                                })}
                            />
                        </Stack>
                    )}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showGuidelines}
                                onChange={(e) => {
                                    dispatch(ShowGuidelinesActions.toggleShowGuidelines());
                                }}
                            />
                        }
                        label="Show Guidelines"
                    />
                </Stack>
            </Stack>
            <ListItemButton style={{ ...CriteriaFilterStyle.MenuStyle }}>
                <ListItemIcon>
                    <CheckIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                </ListItemIcon>
                <ListItemText primary={`Showing Regulations` + `${showGuidelines ? ' and Guidelines' : ''} in Year ${selectedYear}`} />
            </ListItemButton>

            {isLoading ? (
                <Stack style={{ padding: 5 }}>
                    <Loading />
                </Stack>
            ) : (
                <>
                    {regulations
                        .filter((f) => f.Year === selectedYear)
                        .map((risks) => (
                            <CalendarRiskListItem risks={risks} />
                        ))}
                </>
            )}
        </Stack>
    );
};

export default CalendarSurveyRiskList;
